import { useEffect, useState } from "react";
import ClientAvatar from "@/components/ClientAvatar";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Camera, CheckCircle, LogIn, Tag, Loader2, Gift, ArrowUpDown, ArrowUp, ArrowDown, Search, Pencil, CalendarDays, CreditCard, Banknote, Smartphone, Receipt, Clock, DollarSign, Wallet, Copy, ExternalLink, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { QRCodeSVG } from "qrcode.react";
import { cn } from "@/lib/utils";
import { format, parse } from "date-fns";
import ClientPhotoUpload from "@/components/ClientPhotoUpload";
import { BlockDateDialog } from "@/components/AvailabilityManager";
import { Ban, X, Plus } from "lucide-react";
import ManualDepositPopover from "@/components/admin/ManualDepositPopover";
import AddonRequestDialog from "@/components/AddonRequestDialog";
import AddonRequestsList from "@/components/admin/AddonRequestsList";
import ReviewQRDialog from "@/components/ReviewQRDialog";
import { Sparkles } from "lucide-react";
import { isTestAccount } from "@/lib/testAccounts";
import { fmtBusinessDateTime } from "@/lib/timezone";


const timeSlots = [
  "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
  "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM", "5:00 PM", "5:30 PM", "6:00 PM",
];

type SortField = "client" | "service" | "date" | "time" | "price" | "status";
type SortDir = "asc" | "desc";

const Bookings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<any[]>([]);
  const [profiles, setProfiles] = useState<Record<string, any>>({});
  const [manualPaymentTotals, setManualPaymentTotals] = useState<Record<string, number>>({});
  const [pendingAddons, setPendingAddons] = useState<Record<string, { count: number; earliest: string }>>({});
  const [confirmedAddons, setConfirmedAddons] = useState<Record<string, { count: number; names: string[] }>>({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"upcoming" | "confirmed" | "pending" | "cancelled" | "completed" | "addons">("upcoming");
  const [completionPhotoEnabled, setCompletionPhotoEnabled] = useState(false);
  const [checkInEnabled, setCheckInEnabled] = useState(false);
  const [allowDeletePast, setAllowDeletePast] = useState(false);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [showCheckInDialog, setShowCheckInDialog] = useState(false);
  const [checkInBooking, setCheckInBooking] = useState<any>(null);
  const [checkInCouponCode, setCheckInCouponCode] = useState("");
  const [checkInReferralCode, setCheckInReferralCode] = useState("");
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [checkInCouponApplied, setCheckInCouponApplied] = useState<{ code: string; discount_amount: number; id: string } | null>(null);
  const [checkInReferralValid, setCheckInReferralValid] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [editBooking, setEditBooking] = useState<any>(null);
  const [editDate, setEditDate] = useState<Date | undefined>();
  const [editTime, setEditTime] = useState("");
  const [addonBooking, setAddonBooking] = useState<any>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [dateFilter, setDateFilter] = useState<string | null>(null);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentBooking, setPaymentBooking] = useState<any>(null);
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [reviewQrOpen, setReviewQrOpen] = useState(false);
  const [reviewQrBookingId, setReviewQrBookingId] = useState<string | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [stripeCheckoutUrl, setStripeCheckoutUrl] = useState<string | null>(null);
  // Tap to Pay (Stripe Terminal) state
  const [tapToPayLoading, setTapToPayLoading] = useState(false);
  const [tapToPayWaiting, setTapToPayWaiting] = useState(false);
  const [tapToPayIntentId, setTapToPayIntentId] = useState<string | null>(null);
  const [tapToPayClientSecret, setTapToPayClientSecret] = useState<string | null>(null);
  // Collecting payment always marks the booking completed.
  const completeAfterPayment = true;
  // Editable amount, tip, tax
  const [paySubtotal, setPaySubtotal] = useState<string>("0");
  const [payTip, setPayTip] = useState<string>("0");
  const [payTaxRate, setPayTaxRate] = useState<number>(0);
  const [payNotes, setPayNotes] = useState<string>("");
  // Payment portal config (loaded from site_settings)
  const [tipPresets, setTipPresets] = useState<number[]>([15, 20, 25]);
  const [acceptedMethods, setAcceptedMethods] = useState<string[]>([
    "cash",
    "card",
    "zelle",
    "venmo",
    "cashapp",
    "other",
  ]);
  const { toast } = useToast();

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown size={12} className="text-muted-foreground/50" />;
    return sortDir === "asc" ? <ArrowUp size={12} className="text-primary" /> : <ArrowDown size={12} className="text-primary" />;
  };

  const getNetAppointmentTotal = (booking: any) => Math.max(
    0,
    Number(booking.price || 0) - (Number(booking.discount_applied) || 0)
  );

  const getCoveredAppointmentAmount = (booking: any) =>
    (Number(booking.deposit_paid_amount) || 0) + (manualPaymentTotals[booking.id] || 0);

  const isBookingPaidInFull = (booking: any) => {
    const netTotal = getNetAppointmentTotal(booking);
    if (netTotal <= 0) return booking.payment_status === "paid";
    return getCoveredAppointmentAmount(booking) + 0.005 >= netTotal;
  };

  // Fetch payment portal settings + completion photo setting
  useEffect(() => {
    const fetchSettings = async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("key, value")
        .in("key", [
          "require_completion_photo",
          "payment_tip_presets",
          "payment_tax_rate",
          "payment_accepted_methods",
          "allow_admin_delete_past_bookings",
          "check_in_enabled",
        ]);
      if (!data) return;
      const get = (k: string) => data.find((d: any) => d.key === k)?.value;
      setCheckInEnabled(get("check_in_enabled") === "true");
      if (get("require_completion_photo")) setCompletionPhotoEnabled(get("require_completion_photo") === "true");
      setAllowDeletePast(get("allow_admin_delete_past_bookings") === "true");
      const tipsRaw = get("payment_tip_presets");
      if (tipsRaw) {
        try {
          const arr = JSON.parse(tipsRaw);
          if (Array.isArray(arr)) setTipPresets(arr.map(Number).filter((n: number) => !isNaN(n)));
        } catch {
          // fallback: comma-separated
          const arr = String(tipsRaw).split(",").map((x) => Number(x.trim())).filter((n) => !isNaN(n));
          if (arr.length) setTipPresets(arr);
        }
      }
      const taxRaw = get("payment_tax_rate");
      if (taxRaw) setPayTaxRate(Number(taxRaw) || 0);
      const methodsRaw = get("payment_accepted_methods");
      if (methodsRaw) {
        try {
          const arr = JSON.parse(methodsRaw);
          if (Array.isArray(arr) && arr.length > 0) setAcceptedMethods(arr);
        } catch {
          const arr = String(methodsRaw).split(",").map((x) => x.trim()).filter(Boolean);
          if (arr.length) setAcceptedMethods(arr);
        }
      }
    };
    fetchSettings();
  }, []);

  const fetchBookings = async () => {
    let query = supabase
      .from("bookings")
      .select("*")
      .order("booking_date", { ascending: true });

    // Apply server-side status filter for simple status tabs
    if (filter === "confirmed") {
      query = query.eq("status", "confirmed");
    } else if (filter === "cancelled") {
      query = query.eq("status", "cancelled");
    } else if (filter === "pending") {
      query = query.eq("payment_status", "pending").eq("status", "confirmed");
    } else if (filter === "completed") {
      query = query.eq("status", "completed");
    }

    const { data: bookingDataRaw } = await query;
    const bookingData = (bookingDataRaw || []).filter((b: any) => !isTestAccount(b.user_id));
    setBookings(bookingData);

    const userIds = [
      ...new Set(
        (bookingData || []).flatMap((b: any) => [b.user_id, b.deleted_by]).filter(Boolean)
      ),
    ];

    const bookingIds = (bookingData || []).map((b) => b.id);
    if (bookingIds.length > 0) {
      const { data: paymentRows } = await supabase
        .from("manual_payments")
        .select("booking_id, total_amount")
        .in("booking_id", bookingIds)
        .not("booking_id", "is", null);

      const totals: Record<string, number> = {};
      (paymentRows || []).forEach((row: any) => {
        if (!row.booking_id) return;
        totals[row.booking_id] = (totals[row.booking_id] || 0) + (Number(row.total_amount) || 0);
      });
      setManualPaymentTotals(totals);
    } else {
      setManualPaymentTotals({});
    }

    if (userIds.length > 0) {
      const { data: profileData } = await supabase
        .from("profiles")
        .select("user_id, full_name, email, phone, referral_code")
        .in("user_id", userIds);

      const profileMap: Record<string, any> = {};
      (profileData || []).forEach((p) => {
        profileMap[p.user_id] = p;
      });
      setProfiles(profileMap);
    }

    // Pull pending add-ons across all bookings (for the Add-ons tab + badges)
    // Plus confirmed add-ons (for the pulsing "Has Add-on" badge on bookings)
    const { data: addonRows } = await supabase
      .from("booking_addons")
      .select("booking_id, name, created_at, status")
      .in("status", ["pending", "confirmed"])
      .order("created_at", { ascending: true });
    const addonMap: Record<string, { count: number; earliest: string }> = {};
    const confirmedMap: Record<string, { count: number; names: string[] }> = {};
    (addonRows || []).forEach((r: any) => {
      if (r.status === "pending") {
        const cur = addonMap[r.booking_id];
        if (!cur) addonMap[r.booking_id] = { count: 1, earliest: r.created_at };
        else { cur.count += 1; if (r.created_at < cur.earliest) cur.earliest = r.created_at; }
      } else if (r.status === "confirmed") {
        const cur = confirmedMap[r.booking_id];
        if (!cur) confirmedMap[r.booking_id] = { count: 1, names: [r.name] };
        else { cur.count += 1; cur.names.push(r.name); }
      }
    });
    setPendingAddons(addonMap);
    setConfirmedAddons(confirmedMap);

    setLoading(false);
  };

  useEffect(() => {
    fetchBookings();
  }, [filter]);

  // Read ?date= query param to focus on a specific day (e.g. from Dashboard calendar)
  useEffect(() => {
    const dateParam = searchParams.get("date");
    if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)) {
      setDateFilter(dateParam);
    }
  }, [searchParams]);

  // Auto-trigger complete from ?complete= query param
  useEffect(() => {
    const completeId = searchParams.get("complete");
    if (completeId && bookings.length > 0 && !loading) {
      const booking = bookings.find((b) => b.id === completeId);
      if (booking) {
        handleCompleteBooking(booking);
        searchParams.delete("complete");
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [bookings, loading, searchParams]);

  // Auto-open Collect Payment (without auto-completing) from ?pay= query param
  useEffect(() => {
    const payId = searchParams.get("pay");
    if (payId && bookings.length > 0 && !loading) {
      const booking = bookings.find((b) => b.id === payId);
      if (booking) {
        openPaymentDialog(booking, false);
        searchParams.delete("pay");
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [bookings, loading, searchParams]);

  const updateStatus = async (id: string, status: string, options?: { notify?: boolean }) => {
    const notify = options?.notify !== false; // default true to preserve existing behavior
    const booking = bookings.find((b) => b.id === id);
    const { error } = await supabase.from("bookings").update({ status }).eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Updated", description: `Booking ${status}` });
      fetchBookings();

      // Outlook calendar sync (cancel or update)
      if (booking) {
        const profile = profiles[booking.user_id];
        const { syncBookingToOutlook } = await import("@/lib/outlookSync");
        syncBookingToOutlook(
          {
            id: booking.id,
            service_name: booking.service_name,
            service_category: booking.service_category,
            booking_date: booking.booking_date,
            booking_time: booking.booking_time,
            duration: booking.duration,
            status,
            notes: booking.notes,
            client_name: profile?.full_name,
            client_email: profile?.email,
            client_phone: profile?.phone,
          },
          status === "cancelled" ? "cancelled" : "updated",
        ).catch(() => {});
      }

      if (booking && notify) {
        const profile = profiles[booking.user_id];
        if (profile?.phone) {
          supabase.functions.invoke("send-booking-sms", {
            body: {
              type: status === "cancelled" ? "cancellation" : "confirmation",
              phone: profile.phone,
              service_name: booking.service_name,
              booking_date: booking.booking_date,
              booking_time: booking.booking_time,
              duration: booking.duration,
              price: booking.price,
            },
          }).catch((err) => console.error("SMS send failed:", err));
        }

        if (profile?.email) {
          supabase.functions.invoke("send-booking-email", {
            body: {
              type: status === "cancelled" ? "cancellation" : "confirmation",
              email: profile.email,
              service_name: booking.service_name,
              service_category: booking.service_category,
              booking_date: booking.booking_date,
              booking_time: booking.booking_time,
              duration: booking.duration,
              price: booking.price,
              referral_code: profile.referral_code || undefined,
              booking_id: booking.id,
            },
          }).catch((err) => console.error("Email send failed:", err));
        }
      }
    }
  };

  const handleDeleteBooking = async (booking: any) => {
    // Soft delete: keep the appointment (and its history) on record, but mark
    // who removed it and when. Associated data is intentionally preserved.
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("bookings")
      .update({ deleted_at: new Date().toISOString(), deleted_by: auth?.user?.id ?? null })
      .eq("id", booking.id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    setBookings((prev) =>
      prev.map((b) =>
        b.id === booking.id
          ? { ...b, deleted_at: new Date().toISOString(), deleted_by: auth?.user?.id ?? null }
          : b
      )
    );
    toast({
      title: "Appointment deleted",
      description: "It stays in the list marked as deleted and no longer blocks that time.",
    });
  };

  const handleRestoreBooking = async (booking: any) => {
    const { error } = await supabase
      .from("bookings")
      .update({ deleted_at: null, deleted_by: null })
      .eq("id", booking.id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    setBookings((prev) =>
      prev.map((b) => (b.id === booking.id ? { ...b, deleted_at: null, deleted_by: null } : b))
    );
    toast({ title: "Appointment restored" });
  };


  const openPaymentDialog = (booking: any, alsoComplete: boolean) => {
    setPaymentBooking(booking);
    setPaymentMethod("");
    setStripeCheckoutUrl(null);
    // alsoComplete kept for backwards-compat; collecting payment always completes the booking.
    void alsoComplete;
    const balance = Math.max(
      0,
      Number(booking.price || 0) -
        (Number(booking.discount_applied) || 0) -
        (Number(booking.deposit_paid_amount) || 0)
    );
    setPaySubtotal(balance.toFixed(2));
    setPayTip("0");
    setPayNotes("");
    setShowPaymentDialog(true);
  };

  // Convenience wrapper used by "Complete" button (always completes)
  const handleCompleteBooking = (booking: any) => openPaymentDialog(booking, true);

  // Tap to Pay: create a card_present PaymentIntent. The native Stripe Terminal SDK
  // will collect the tap; on web we show a "Waiting for native SDK" modal so the flow
  // is wired end-to-end. "Simulate Tap Success" lets us verify the booking gets marked
  // paid before the native build is in place.
  const handleStartTapToPay = async () => {
    if (!paymentBooking) return;
    const subtotalNum = Math.max(0, Number(paySubtotal) || 0);
    const tipNum = Math.max(0, Number(payTip) || 0);
    const taxNum = +(subtotalNum * (payTaxRate / 100)).toFixed(2);
    const totalNum = +(subtotalNum + tipNum + taxNum).toFixed(2);

    if (totalNum <= 0) {
      toast({ title: "Enter an amount", description: "Total must be greater than $0.", variant: "destructive" });
      return;
    }

    setTapToPayLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("terminal-create-payment-intent", {
        body: {
          action: "create",
          subtotal: subtotalNum,
          tip_amount: tipNum,
          tax_amount: taxNum,
          total_amount: totalNum,
          user_id: paymentBooking.user_id,
          booking_id: paymentBooking.id,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setTapToPayIntentId(data?.payment_intent_id || null);
      setTapToPayClientSecret(data?.client_secret || null);
      setTapToPayWaiting(true);
      toast({ title: "Ready for tap", description: "Hand the device to the client and tap their card." });
    } catch (err: any) {
      toast({ title: "Tap to Pay error", description: err?.message || "Failed to start payment", variant: "destructive" });
    } finally {
      setTapToPayLoading(false);
    }
  };

  // Web placeholder: marks the in-progress Tap to Pay as successful and records it
  // through the same manual_payments path used by other methods. Replace this with
  // the native SDK confirmation callback once the iOS/Android build is wired up.
  const handleSimulateTapSuccess = async () => {
    setPaymentMethod("tap_to_pay");
    setTapToPayWaiting(false);
    await handlePaymentComplete();
    setTapToPayIntentId(null);
    setTapToPayClientSecret(null);
  };

  const handlePaymentComplete = async (methodOverride?: string) => {
    const method = methodOverride || paymentMethod;
    if (!paymentBooking || !method) return;
    setPaymentLoading(true);

    const subtotalNum = Math.max(0, Number(paySubtotal) || 0);
    const tipNum = Math.max(0, Number(payTip) || 0);
    const taxNum = +(subtotalNum * (payTaxRate / 100)).toFixed(2);
    const totalNum = +(subtotalNum + tipNum + taxNum).toFixed(2);

    if (method === "card" && !stripeCheckoutUrl) {
      // Card Terminal: generate Stripe Checkout — open in new tab + show QR for client to scan
      try {
        const { data, error } = await supabase.functions.invoke("create-checkout", {
          body: {
            service_name: paymentBooking.service_name,
            service_category: paymentBooking.service_category,
            service_price: totalNum,
            booking_date: paymentBooking.booking_date,
            booking_time: paymentBooking.booking_time,
            duration: paymentBooking.duration,
            payment_type: "full",
            user_id: paymentBooking.user_id,
            booking_id: paymentBooking.id,
          },
        });

        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        if (data?.url) {
          setStripeCheckoutUrl(data.url);
          window.open(data.url, "_blank");
          toast({ title: "Card Terminal Ready", description: "Checkout opened in new tab. Client can also scan the QR code below. Tap Confirm Payment once paid." });
        } else {
          throw new Error("No checkout URL returned");
        }
      } catch (err: any) {
        toast({ title: "Error", description: err.message || "Failed to create card checkout", variant: "destructive" });
      }
      setPaymentLoading(false);
      // Keep dialog open so admin can show QR / copy link, then click Confirm Payment
      return;
    }

    // 1. Log the manual payment
    const { data: { user: authUser } } = await supabase.auth.getUser();
    await supabase.from("manual_payments").insert({
      booking_id: paymentBooking.id,
      user_id: paymentBooking.user_id,
      collected_by: authUser?.id,
      method: method,
      subtotal: subtotalNum,
      tip_amount: tipNum,
      tax_amount: taxNum,
      total_amount: totalNum,
      notes: payNotes,
    });

    // 2. Update booking — record tip/tax + mark paid; only flip status to completed if the admin asked us to
    const updateData: any = {
      payment_status: "paid",
      payment_type: method,
      tip_amount: tipNum,
      tax_amount: taxNum,
    };
    if (completeAfterPayment) updateData.status = "completed";

    const { error } = await supabase.from("bookings").update(updateData).eq("id", paymentBooking.id);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      if (completeAfterPayment && completionPhotoEnabled) {
        setSelectedBooking(paymentBooking);
        setShowPhotoDialog(true);
      }
      toast({
        title: completeAfterPayment ? "Booking Completed" : "Payment Collected",
        description: `$${totalNum.toFixed(2)} via ${method}`,
      });
      fetchBookings();

      // Auto-send payment receipt (email + SMS) to client
      supabase.functions.invoke("send-payment-receipt", {
        body: {
          user_id: paymentBooking.user_id,
          booking_id: paymentBooking.id,
          service_name: paymentBooking.service_name,
          booking_date: paymentBooking.booking_date,
          booking_time: paymentBooking.booking_time,
          method: method,
          subtotal: subtotalNum,
          tip_amount: tipNum,
          tax_amount: taxNum,
          total_amount: totalNum,
          notes: payNotes,
        },
      }).catch((e) => console.error("Receipt send failed:", e));

      // Send thank-you email only when finalizing the appointment
      if (completeAfterPayment) {
        const profile = profiles[paymentBooking.user_id];
        if (profile?.email) {
          supabase.functions.invoke("send-booking-email", {
            body: {
              type: "completion",
              email: profile.email,
              client_name: profile.full_name || "",
              service_name: paymentBooking.service_name,
              service_category: paymentBooking.service_category,
              booking_date: paymentBooking.booking_date,
              booking_time: paymentBooking.booking_time,
              duration: paymentBooking.duration,
              price: paymentBooking.price,
              discount_applied: paymentBooking.discount_applied || 0,
              payment_type: method,
              referral_code: profile.referral_code || "",
              booking_id: paymentBooking.id,
            },
          }).catch(() => {});
        }
      }

      // Show QR code popup so admin can hand the device/screen to client to scan
      setReviewQrBookingId(paymentBooking.id);
      setReviewQrOpen(true);
    }

    setPaymentLoading(false);
    setShowPaymentDialog(false);
    setPaymentBooking(null);
  };

  const handlePhotoDialogComplete = () => {
    if (selectedBooking) {
      updateStatus(selectedBooking.id, "completed");
    }
    setShowPhotoDialog(false);
    setSelectedBooking(null);
  };

  const handleSkipPhoto = () => {
    if (selectedBooking) {
      updateStatus(selectedBooking.id, "completed");
    }
    setShowPhotoDialog(false);
    setSelectedBooking(null);
  };

  const handleCheckIn = (booking: any) => {
    setCheckInBooking(booking);
    setCheckInCouponCode("");
    setCheckInReferralCode("");
    setCheckInCouponApplied(null);
    setCheckInReferralValid(false);
    setShowCheckInDialog(true);
  };

  const handleApplyCheckInCoupon = async () => {
    if (!checkInBooking || !checkInCouponCode) return;
    setCheckInLoading(true);
    try {
      const { data: coupon } = await supabase
        .from("coupons")
        .select("*")
        .eq("code", checkInCouponCode)
        .eq("user_id", checkInBooking.user_id)
        .eq("is_used", false)
        .maybeSingle();

      if (!coupon) {
        toast({ title: "Invalid", description: "Coupon not found, already used, or doesn't belong to this client", variant: "destructive" });
      } else if (new Date(coupon.expires_at) < new Date()) {
        toast({ title: "Expired", description: "This coupon has expired", variant: "destructive" });
      } else {
        setCheckInCouponApplied({ code: coupon.code, discount_amount: coupon.discount_amount, id: coupon.id });
        toast({ title: "Applied", description: `$${coupon.discount_amount} coupon applied` });
      }
    } catch {
      toast({ title: "Error", description: "Failed to apply coupon", variant: "destructive" });
    }
    setCheckInLoading(false);
  };

  const handleValidateReferral = async () => {
    if (!checkInReferralCode) return;
    setCheckInLoading(true);
    const { data: referrerProfile } = await supabase
      .from("profiles")
      .select("user_id, full_name")
      .eq("referral_code", checkInReferralCode.toUpperCase())
      .maybeSingle();

    if (referrerProfile && referrerProfile.user_id !== checkInBooking?.user_id) {
      setCheckInReferralValid(true);
      toast({ title: "Valid", description: `Referral from ${referrerProfile.full_name || "client"}` });
    } else {
      setCheckInReferralValid(false);
      toast({ title: "Invalid", description: "Referral code not found or is the client's own code", variant: "destructive" });
    }
    setCheckInLoading(false);
  };

  const handleConfirmCheckIn = async () => {
    if (!checkInBooking) return;
    setCheckInLoading(true);

    // Mark coupon as used
    if (checkInCouponApplied) {
      await supabase.from("coupons").update({ is_used: true, used_at: new Date().toISOString(), booking_id: checkInBooking.id }).eq("id", checkInCouponApplied.id);
    }

    // Record referral entry
    if (checkInReferralValid && checkInReferralCode) {
      const { data: referrerProfile } = await supabase
        .from("profiles")
        .select("user_id")
        .eq("referral_code", checkInReferralCode.toUpperCase())
        .maybeSingle();

      if (referrerProfile) {
        await supabase.from("referral_entries").insert({
          referrer_user_id: referrerProfile.user_id,
          referred_user_id: checkInBooking.user_id,
        });
      }
    }

    // Update booking with check-in and discount
    const updateData: any = {
      checked_in_at: new Date().toISOString(),
    };
    if (checkInCouponApplied) {
      updateData.discount_applied = checkInCouponApplied.discount_amount;
      updateData.coupon_id = checkInCouponApplied.id;
    }
    if (checkInReferralValid) {
      updateData.referral_code_used = checkInReferralCode.toUpperCase();
    }

    await supabase.from("bookings").update(updateData).eq("id", checkInBooking.id);

    toast({ title: "Checked In", description: `${profiles[checkInBooking.user_id]?.full_name || "Client"} has been checked in` });
    setShowCheckInDialog(false);
    setCheckInBooking(null);
    fetchBookings();
    setCheckInLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl font-bold italic">Bookings</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage all client appointments</p>
        </div>
        <Button
          onClick={() => navigate("/book")}
          className="text-xs tracking-widest uppercase gap-1.5"
        >
          <Plus size={14} /> New Booking
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
      <div className="flex gap-1 overflow-x-auto pb-1 border-b border-border/50">
          {([
            ["upcoming", "Upcoming"],
            ["confirmed", "Confirmed"],
            ["pending", "Pending"],
            ["addons", "Add-on Requests"],
            ["cancelled", "Cancelled"],
            ["completed", "Completed"],
          ] as [typeof filter, string][]).map(([f, label]) => {
            const addonBadge = f === "addons" ? Object.keys(pendingAddons).length : 0;
            return (
              <Button
                key={f}
                variant="ghost"
                size="sm"
                onClick={() => setFilter(f)}
                className={cn(
                  "text-xs tracking-widest uppercase rounded-none border-b-2 px-4 gap-1.5",
                  filter === f
                    ? "border-b-primary text-primary bg-primary/5"
                    : "border-b-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                {label}
                {f === "addons" && addonBadge > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-primary text-primary-foreground rounded-full">
                    {addonBadge}
                  </span>
                )}
              </Button>
            );
          })}
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Camera size={14} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Completion Photo</span>
            <Switch
              checked={completionPhotoEnabled}
              onCheckedChange={async (checked) => {
                setCompletionPhotoEnabled(checked);
                const { data: existing } = await supabase
                  .from("site_settings")
                  .select("id")
                  .eq("key", "require_completion_photo")
                  .maybeSingle();
                if (existing) {
                  await supabase.from("site_settings").update({ value: checked ? "true" : "false" }).eq("key", "require_completion_photo");
                } else {
                  await supabase.from("site_settings").insert({ key: "require_completion_photo", value: checked ? "true" : "false" });
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Active date filter banner (from Dashboard calendar click) */}
      {dateFilter && (
        <div className="flex flex-wrap items-center justify-between gap-3 p-3 border border-primary/30 bg-primary/5">
          <div className="flex items-center gap-2 text-sm">
            <CalendarDays size={14} className="text-primary" />
            <span className="font-medium">
              Showing {format(parse(dateFilter, "yyyy-MM-dd", new Date()), "EEEE, MMM d, yyyy")}
            </span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={() => navigate(`/book?date=${dateFilter}`)}
              className="text-xs gap-1.5"
            >
              <Plus size={13} /> Add Appointment
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBlockDialogOpen(true)}
              className="text-xs gap-1.5"
            >
              <Ban size={13} /> Block This Date
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDateFilter(null);
                searchParams.delete("date");
                setSearchParams(searchParams, { replace: true });
              }}
              className="text-xs gap-1.5"
            >
              <X size={13} /> Clear
            </Button>
          </div>
        </div>
      )}

      {/* Search & Sort */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search client, service, date..."
            className="pl-9 bg-background border-border/50 text-sm"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {([
            ["date", "Date"],
            ["client", "Client"],
            ["price", "Price"],
            ["status", "Status"],
          ] as [SortField, string][]).map(([field, label]) => (
            <Button
              key={field}
              variant="ghost"
              size="sm"
              onClick={() => handleSort(field)}
              className={cn(
                "text-xs gap-1 h-8",
                sortField === field ? "text-primary bg-primary/10" : "text-muted-foreground"
              )}
            >
              {label} <SortIcon field={field} />
            </Button>
          ))}
        </div>
      </div>

      {/* Bookings List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-muted-foreground" />
        </div>
      ) : (() => {
        const today = new Date().toISOString().split("T")[0];
        const q = searchQuery.toLowerCase();
        const filtered = bookings.filter((b) => {
          // Hard date filter (from Dashboard calendar) takes priority over the tab
          if (dateFilter) {
            if (b.booking_date !== dateFilter) return false;
          } else {
            if (filter === "upcoming" && (b.booking_date < today || b.status === "cancelled")) return false;
            if (filter === "completed" && b.status !== "completed") return false;
            if (filter === "addons" && !pendingAddons[b.id]) return false;
          }

          if (!q) return true;
          const profile = profiles[b.user_id];
          return (
            (profile?.full_name || "").toLowerCase().includes(q) ||
            (profile?.email || "").toLowerCase().includes(q) ||
            b.service_name.toLowerCase().includes(q) ||
            b.service_category.toLowerCase().includes(q) ||
            b.booking_date.includes(q) ||
            b.booking_time.toLowerCase().includes(q) ||
            b.status.toLowerCase().includes(q)
          );
        });

        const sorted = [...filtered].sort((a, b) => {
          // On the Add-ons tab, sort by earliest pending request time (oldest first)
          if (filter === "addons" && !dateFilter) {
            const ea = pendingAddons[a.id]?.earliest || "";
            const eb = pendingAddons[b.id]?.earliest || "";
            return ea.localeCompare(eb);
          }
          const dir = sortDir === "asc" ? 1 : -1;
          switch (sortField) {
            case "client":
              return dir * ((profiles[a.user_id]?.full_name || "").localeCompare(profiles[b.user_id]?.full_name || ""));
            case "service":
              return dir * a.service_name.localeCompare(b.service_name);
            case "date":
              return dir * (a.booking_date.localeCompare(b.booking_date) || a.booking_time.localeCompare(b.booking_time));
            case "time":
              return dir * a.booking_time.localeCompare(b.booking_time);
            case "price":
              return dir * (Number(a.price) - Number(b.price));
            case "status":
              return dir * a.status.localeCompare(b.status);
            default:
              return 0;
          }
        });

        if (sorted.length === 0) {
          return (
            <div className="text-center py-16">
              <CalendarDays size={40} className="mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground text-sm">No bookings found.</p>
            </div>
          );
        }

        // Group by date
        const grouped: Record<string, typeof sorted> = {};
        sorted.forEach((b) => {
          if (!grouped[b.booking_date]) grouped[b.booking_date] = [];
          grouped[b.booking_date].push(b);
        });

        // Calculate pending total (confirmed bookings)
        const pendingTotal = sorted
          .filter((b) => b.status === "confirmed")
          .reduce((sum, b) => sum + (Number(b.price) - (Number(b.discount_applied) || 0)), 0);
        const pendingCount = sorted.filter((b) => b.status === "confirmed").length;

        const PendingBanner = () => pendingCount > 0 ? (
          <div className="flex items-center justify-between px-4 py-3 rounded-lg bg-primary/10 border border-primary/20">
            <div className="flex items-center gap-2">
              <Receipt size={16} className="text-primary" />
              <span className="text-sm font-medium">Pending Total</span>
              <span className="text-xs text-muted-foreground">({pendingCount} booking{pendingCount !== 1 ? "s" : ""})</span>
            </div>
            <span className="text-lg font-bold text-primary">${pendingTotal.toFixed(0)}</span>
          </div>
        ) : null;

        // Calculate completed/collected total
        const completedTotal = sorted
          .filter((b) => b.status === "completed")
          .reduce((sum, b) => sum + (Number(b.price) - (Number(b.discount_applied) || 0)), 0);
        const completedCount = sorted.filter((b) => b.status === "completed").length;

        const CompletedBanner = () => filter === "completed" && completedCount > 0 ? (
          <div className="flex items-center justify-between px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <div className="flex items-center gap-2">
              <DollarSign size={16} className="text-emerald-600" />
              <span className="text-sm font-medium">Total Collected</span>
              <span className="text-xs text-muted-foreground">({completedCount} booking{completedCount !== 1 ? "s" : ""})</span>
            </div>
            <span className="text-lg font-bold text-emerald-600">${completedTotal.toFixed(0)}</span>
          </div>
        ) : null;

        return (
          <div className="space-y-6">
            <PendingBanner />
            <CompletedBanner />
            {Object.entries(grouped).map(([date, dateBookings]) => {
              const dateObj = new Date(date + "T00:00:00");
              const isToday = new Date().toISOString().split("T")[0] === date;
              const isPast = dateObj < new Date(new Date().toISOString().split("T")[0] + "T00:00:00");

              return (
                <div key={date}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={cn(
                      "flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium tracking-wide",
                      isToday ? "bg-primary text-primary-foreground" : isPast ? "bg-muted text-muted-foreground" : "bg-accent text-accent-foreground"
                    )}>
                      <CalendarDays size={12} />
                      {isToday ? "Today" : format(dateObj, "EEE, MMM d, yyyy")}
                    </div>
                    <span className="text-xs text-muted-foreground">{dateBookings.length} booking{dateBookings.length !== 1 ? "s" : ""}</span>
                    <div className="flex-1 h-px bg-border/50" />
                  </div>

                  <div className="grid gap-3">
                    {dateBookings.map((b) => {
                      const profile = profiles[b.user_id];
                      const hasConfirmedAddon = !!confirmedAddons[b.id];
                      const statusStyles = b.status === "confirmed"
                        ? "border-l-primary"
                        : b.status === "completed"
                        ? "border-l-emerald-500"
                        : "border-l-destructive";

                      return (
                        <Card key={b.id} className={cn(
                          "bg-card border-border/50 border-l-4 overflow-hidden transition-all hover:shadow-md cursor-pointer",
                          statusStyles,
                          hasConfirmedAddon && "ring-2 ring-amber-400/60 ring-offset-1 ring-offset-background animate-pulse",
                        )} onClick={() => navigate(`/admin/clients/${b.user_id}`)}>
                          <CardContent className="p-4">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                              {/* Time badge */}
                              <div className="shrink-0 flex sm:flex-col items-center sm:items-center gap-2 sm:gap-0 sm:w-20 sm:text-center">
                                <Clock size={14} className="text-muted-foreground sm:mb-1" />
                                <span className="text-sm font-semibold">{b.booking_time}</span>
                                <span className="text-[10px] text-muted-foreground">{b.duration}</span>
                              </div>

                              {/* Main info */}
                              <div className="flex-1 min-w-0">
                                  <div className="flex items-start gap-2">
                                    <div className="shrink-0 text-left min-w-[50px]">
                                      <p className="font-bold text-sm">${Number(b.price).toFixed(0)}</p>
                                      {Number(b.discount_applied) > 0 && (
                                        <p className="text-[10px] text-primary">-${Number(b.discount_applied)} off</p>
                                      )}
                                    </div>
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        <h3 className="font-semibold text-sm truncate">{b.service_name}</h3>
                                        {(b as any).created_by_admin && (
                                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 font-medium tracking-wide uppercase shrink-0">
                                            Admin
                                          </span>
                                        )}
                                        {hasConfirmedAddon && (
                                          <span
                                            className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 font-semibold tracking-wide uppercase shrink-0 flex items-center gap-1 border border-amber-500/40"
                                            title={confirmedAddons[b.id].names.join(", ")}
                                          >
                                            <Sparkles size={10} /> +{confirmedAddons[b.id].count} Add-on{confirmedAddons[b.id].count > 1 ? "s" : ""}
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-xs text-muted-foreground mt-0.5">{b.service_category}</p>
                                      {b.deleted_at && (
                                        <p className="text-[11px] text-destructive mt-1 italic">
                                          Deleted by {profiles[b.deleted_by]?.full_name || "an admin"} on {fmtBusinessDateTime(b.deleted_at)}
                                        </p>
                                      )}

                                      {hasConfirmedAddon && (
                                        <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-1 italic">
                                          + {confirmedAddons[b.id].names.join(" · ")}
                                        </p>
                                      )}
                                    </div>
                                  </div>


                                {/* Client row */}
                                <div className="flex items-center gap-2 mt-2">
                                  <ClientAvatar userId={b.user_id} name={profile?.full_name} size="sm" />
                                  <span className="text-sm truncate">{profile?.full_name || "Unknown"}</span>
                                  {profile?.phone && (
                                    <span className="text-[10px] text-muted-foreground hidden sm:inline">{profile.phone}</span>
                                  )}
                                </div>
                              </div>

                              {/* Status & Actions */}
                              <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap shrink-0" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center gap-1.5">
                                  {b.deleted_at && (
                                    <span
                                      className="text-[10px] px-2.5 py-1 rounded-full font-medium tracking-wide uppercase bg-destructive/20 text-destructive border border-destructive/40"
                                      title={`Deleted ${fmtBusinessDateTime(b.deleted_at)}${
                                        b.deleted_by ? ` by ${profiles[b.deleted_by]?.full_name || "an admin"}` : ""
                                      }`}
                                    >
                                      Deleted
                                    </span>
                                  )}
                                  <span className={cn(
                                    "text-[10px] px-2.5 py-1 rounded-full font-medium tracking-wide uppercase",
                                    b.status === "confirmed" ? "bg-primary/15 text-primary" :
                                    b.status === "completed" ? "bg-emerald-500/15 text-emerald-600" :
                                    "bg-destructive/15 text-destructive"
                                  )}>
                                    {b.status}
                                  </span>

                                  {checkInEnabled && b.checked_in_at && (
                                    <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-600 flex items-center gap-0.5 font-medium">
                                      <LogIn size={10} /> In
                                    </span>
                                  )}
                                  {isBookingPaidInFull(b) ? (
                                    <span
                                      className="text-[10px] px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-600 flex items-center gap-0.5 font-medium"
                                      title={`Paid in full: $${getCoveredAppointmentAmount(b).toFixed(2)} covered`}
                                    >
                                      <DollarSign size={10} /> Paid in Full
                                    </span>
                                  ) : Number(b.deposit_paid_amount) > 0 ? (
                                    <ManualDepositPopover
                                      booking={b}
                                      onSaved={fetchBookings}
                                      trigger={
                                        <button
                                          type="button"
                                          className="text-[10px] px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25 flex items-center gap-0.5 font-medium transition-colors cursor-pointer"
                                          title={`Deposit received: $${Number(b.deposit_paid_amount).toFixed(2)} — click to edit`}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <DollarSign size={10} /> Deposit Received ${Number(b.deposit_paid_amount).toFixed(0)}
                                        </button>
                                      }
                                    />
                                  ) : b.status === "confirmed" && (
                                    <ManualDepositPopover
                                      booking={b}
                                      onSaved={fetchBookings}
                                      trigger={
                                        <button
                                          type="button"
                                          className="text-[10px] px-2 py-1 rounded-full bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary flex items-center gap-0.5 font-medium transition-colors cursor-pointer"
                                          title="No deposit collected — click to add"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <Plus size={10} /> Add deposit
                                        </button>
                                      }
                                    />
                                  )}
                                </div>

                                <div className="flex gap-1.5">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                                    onClick={() => {
                                      setEditBooking(b);
                                      setEditDate(new Date(b.booking_date + "T00:00:00"));
                                      setEditTime(b.booking_time);
                                    }}
                                    title="Edit"
                                  >
                                    <Pencil size={14} />
                                  </Button>
                                  {b.status === "confirmed" && (
                                    <>
                                      {checkInEnabled && !b.checked_in_at && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-8 text-xs gap-1 border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/10"
                                          onClick={() => handleCheckIn(b)}
                                        >
                                          <LogIn size={12} /> Check In
                                        </Button>
                                      )}
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-8 text-xs gap-1 border-primary/40 text-primary hover:bg-primary/10"
                                        onClick={() => openPaymentDialog(b, false)}
                                        title="Collect payment without finalizing the booking"
                                      >
                                        <Wallet size={12} /> Collect
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-8 text-xs gap-1"
                                        onClick={() => setAddonBooking(b)}
                                        title="Add or review add-ons"
                                      >
                                        <Sparkles size={12} /> Add-on
                                      </Button>
                                      <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                          <Button size="sm" variant="destructive" className="h-8 text-xs">
                                            Cancel
                                          </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent className="bg-card border-border/50">
                                          <AlertDialogHeader>
                                            <AlertDialogTitle className="font-serif text-2xl font-bold italic">
                                              Cancel Appointment?
                                            </AlertDialogTitle>
                                            <AlertDialogDescription className="text-muted-foreground font-light leading-relaxed">
                                              Cancel <span className="font-medium text-foreground">{b.service_name}</span> for{" "}
                                              <span className="font-medium text-foreground">{profiles[b.user_id]?.full_name || "this client"}</span>{" "}
                                              on <span className="font-medium text-foreground">{format(new Date(b.booking_date + "T00:00:00"), "MMM d, yyyy")}</span>?
                                              <br /><br />
                                              Would you like to notify the client by email{profiles[b.user_id]?.phone ? " and SMS" : ""}?
                                            </AlertDialogDescription>
                                          </AlertDialogHeader>
                                          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                                            <AlertDialogCancel className="rounded-none mt-0">Keep</AlertDialogCancel>
                                            <AlertDialogAction
                                              onClick={() => updateStatus(b.id, "cancelled", { notify: false })}
                                              className="rounded-none bg-muted text-foreground hover:bg-muted/80"
                                            >
                                              Cancel without Email
                                            </AlertDialogAction>
                                            <AlertDialogAction
                                              onClick={() => updateStatus(b.id, "cancelled", { notify: true })}
                                              className="rounded-none bg-destructive hover:bg-destructive/90"
                                            >
                                              Cancel & Email Client
                                            </AlertDialogAction>
                                          </AlertDialogFooter>
                                        </AlertDialogContent>
                                      </AlertDialog>
                                    </>
                                  )}
                                  {b.status === "cancelled" && (
                                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => updateStatus(b.id, "confirmed")}>
                                      Restore
                                    </Button>
                                  )}
                                  {b.deleted_at ? (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-8 text-xs"
                                      onClick={() => handleRestoreBooking(b)}
                                      title="Undo deletion"
                                    >
                                      Undo Delete
                                    </Button>
                                  ) : allowDeletePast && (b.status === "cancelled" || b.status === "completed") && (
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10" title="Delete appointment">
                                          <Trash2 size={14} />
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent className="bg-card border-border/50">
                                        <AlertDialogHeader>
                                          <AlertDialogTitle className="font-serif text-2xl font-bold italic">
                                            Delete Appointment?
                                          </AlertDialogTitle>
                                          <AlertDialogDescription className="text-muted-foreground font-light leading-relaxed">
                                            This removes <span className="font-medium text-foreground">{b.service_name}</span> for{" "}
                                            <span className="font-medium text-foreground">{profiles[b.user_id]?.full_name || "this client"}</span>{" "}
                                            on <span className="font-medium text-foreground">{format(new Date(b.booking_date + "T00:00:00"), "MMM d, yyyy")}</span>.
                                            It stays in this list marked as deleted (with your name and the time), frees up the time slot,
                                            and can be undone at any point.
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel className="rounded-none">Keep</AlertDialogCancel>
                                          <AlertDialogAction
                                            onClick={() => handleDeleteBooking(b)}
                                            className="rounded-none bg-destructive hover:bg-destructive/90"
                                          >
                                            Delete Appointment
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  )}

                                </div>
                              </div>
                            </div>
                            <div className="px-4 pb-3">
                              <AddonRequestsList bookingId={b.id} onChange={fetchBookings} />
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <PendingBanner />
            <CompletedBanner />
          </div>
        );
      })()}

      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent className="bg-card border-border/50 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-bold italic flex items-center gap-2">
              <Camera size={20} className="text-primary" />
              Upload Completion Photo
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              Take a photo of the completed work for {selectedBooking?.service_name}.
            </DialogDescription>
          </DialogHeader>

          {selectedBooking && (
            <div className="py-4">
              <ClientPhotoUpload
                userId={selectedBooking.user_id}
                category="completion"
                bookingId={selectedBooking.id}
                uploadedBy="admin"
                label="Completed Work Photo"
              />
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button
              variant="ghost"
              onClick={handleSkipPhoto}
              className="text-xs tracking-widest uppercase text-muted-foreground"
            >
              Skip
            </Button>
            <Button
              onClick={handlePhotoDialogComplete}
              className="text-xs tracking-widest uppercase"
            >
              Complete Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Check-In Dialog */}
      <Dialog open={showCheckInDialog} onOpenChange={setShowCheckInDialog}>
        <DialogContent className="bg-card border-border/50 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-bold italic flex items-center gap-2">
              <LogIn size={20} className="text-green-600" />
              Client Check-In
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              Check in {checkInBooking && profiles[checkInBooking.user_id]?.full_name} for {checkInBooking?.service_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Coupon Code */}
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2 flex items-center gap-2">
                <Tag size={14} /> Coupon Code
              </p>
              {checkInCouponApplied ? (
                <div className="flex items-center justify-between p-3 border border-primary/30 bg-primary/5">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-primary" />
                    <code className="font-mono font-bold text-xs tracking-widest">{checkInCouponApplied.code}</code>
                    <span className="text-sm text-primary font-semibold">-${checkInCouponApplied.discount_amount}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setCheckInCouponApplied(null)} className="text-xs text-muted-foreground">Remove</Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={checkInCouponCode}
                    onChange={(e) => setCheckInCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter coupon code"
                    className="bg-background border-border/50 font-mono tracking-widest uppercase"
                  />
                  <Button variant="outline" size="sm" disabled={checkInLoading || checkInCouponCode.length < 3} onClick={handleApplyCheckInCoupon} className="text-xs tracking-widest uppercase shrink-0">
                    {checkInLoading ? <Loader2 size={14} className="animate-spin" /> : "Apply"}
                  </Button>
                </div>
              )}
            </div>

            {/* Referral Code */}
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2 flex items-center gap-2">
                <Gift size={14} /> Referral Code
              </p>
              {checkInReferralValid ? (
                <div className="flex items-center justify-between p-3 border border-green-500/30 bg-green-500/5">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-600" />
                    <code className="font-mono font-bold text-xs tracking-widest">{checkInReferralCode.toUpperCase()}</code>
                    <span className="text-sm text-green-600 font-semibold">Valid</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setCheckInReferralValid(false); setCheckInReferralCode(""); }} className="text-xs text-muted-foreground">Remove</Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={checkInReferralCode}
                    onChange={(e) => setCheckInReferralCode(e.target.value.toUpperCase())}
                    placeholder="Enter referral code"
                    className="bg-background border-border/50 font-mono tracking-widest uppercase"
                  />
                  <Button variant="outline" size="sm" disabled={checkInLoading || checkInReferralCode.length < 3} onClick={handleValidateReferral} className="text-xs tracking-widest uppercase shrink-0">
                    {checkInLoading ? <Loader2 size={14} className="animate-spin" /> : "Verify"}
                  </Button>
                </div>
              )}
            </div>

            {/* Summary */}
            {checkInBooking && (
              <div className="border-t border-border pt-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Service Price</span>
                  <span>${Number(checkInBooking.price).toFixed(0)}</span>
                </div>
                {checkInCouponApplied && (
                  <div className="flex justify-between text-primary">
                    <span>Coupon Discount</span>
                    <span>-${checkInCouponApplied.discount_amount}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold border-t border-border pt-2">
                  <span>Due</span>
                  <span>${Math.max(0, Number(checkInBooking.price) - (checkInCouponApplied?.discount_amount || 0)).toFixed(0)}</span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="ghost" onClick={() => setShowCheckInDialog(false)} className="text-xs tracking-widest uppercase text-muted-foreground">
              Cancel
            </Button>
            <Button onClick={handleConfirmCheckIn} disabled={checkInLoading} className="text-xs tracking-widest uppercase gap-1">
              {checkInLoading ? <Loader2 size={14} className="animate-spin" /> : <><LogIn size={14} /> Check In</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Date/Time Dialog */}
      <Dialog open={!!editBooking} onOpenChange={(open) => !open && setEditBooking(null)}>
        <DialogContent className="bg-card border-border/50 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-bold italic flex items-center gap-2">
              <Pencil size={18} className="text-primary" />
              Reschedule Appointment
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              {editBooking && `${editBooking.service_name} — ${profiles[editBooking.user_id]?.full_name || "Client"}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">Date</p>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !editDate && "text-muted-foreground")}>
                    <CalendarDays size={14} className="mr-2" />
                    {editDate ? format(editDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={editDate}
                    onSelect={setEditDate}
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">Time</p>
              <Select value={editTime} onValueChange={setEditTime}>
                <SelectTrigger className="bg-background border-border/50">
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="ghost" onClick={() => setEditBooking(null)} className="text-xs tracking-widest uppercase text-muted-foreground">
              Cancel
            </Button>
            <Button
              disabled={editSaving || !editDate || !editTime}
              onClick={async () => {
                if (!editBooking || !editDate || !editTime) return;
                setEditSaving(true);
                const newDate = editDate.toISOString().split("T")[0];
                const saveReschedule = (override: boolean) =>
                  supabase
                    .from("bookings")
                    .update({ booking_date: newDate, booking_time: editTime, blocked_day_override: override })
                    .eq("id", editBooking.id);
                let { error } = await saveReschedule(false);
                if (error && /too close to another appointment|unavailable for booking/i.test(error.message)) {
                  if (window.confirm(`${error.message}\n\nReschedule to this time anyway?`)) {
                    ({ error } = await saveReschedule(true));
                  } else {
                    setEditSaving(false);
                    return;
                  }
                }
                if (error) {
                  toast({ title: "Error", description: error.message, variant: "destructive" });
                } else {
                  toast({ title: "Updated", description: `Rescheduled to ${format(editDate, "MMM d, yyyy")} at ${editTime}` });

                  // Notify client
                  const profile = profiles[editBooking.user_id];
                  if (profile?.email) {
                    supabase.functions.invoke("send-booking-email", {
                      body: {
                        type: "confirmation",
                        email: profile.email,
                        service_name: editBooking.service_name,
                        service_category: editBooking.service_category,
                        booking_date: newDate,
                        booking_time: editTime,
                        duration: editBooking.duration,
                        price: editBooking.price,
                        referral_code: profile.referral_code || undefined,
                        booking_id: editBooking.id,
                      },
                    }).catch(() => {});
                  }
                  if (profile?.phone) {
                    supabase.functions.invoke("send-booking-sms", {
                      body: {
                        type: "confirmation",
                        phone: profile.phone,
                        service_name: editBooking.service_name,
                        booking_date: newDate,
                        booking_time: editTime,
                        duration: editBooking.duration,
                        price: editBooking.price,
                      },
                    }).catch(() => {});
                  }

                  setEditBooking(null);
                  fetchBookings();
                }
                setEditSaving(false);
              }}
              className="text-xs tracking-widest uppercase gap-1"
            >
              {editSaving ? <Loader2 size={14} className="animate-spin" /> : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Collection Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={(open) => { if (!open) { setShowPaymentDialog(false); setPaymentBooking(null); } }}>
        <DialogContent className="bg-card border-border/50 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-bold italic flex items-center gap-2">
              <Receipt size={20} className="text-primary" />
              {completeAfterPayment ? "Complete & Collect Payment" : "Collect Payment"}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              {paymentBooking && `${paymentBooking.service_name} — ${profiles[paymentBooking.user_id]?.full_name || "Client"}`}
            </DialogDescription>
          </DialogHeader>

          {paymentBooking && (() => {
            const subtotalNum = Math.max(0, Number(paySubtotal) || 0);
            const tipNum = Math.max(0, Number(payTip) || 0);
            const taxNum = +(subtotalNum * (payTaxRate / 100)).toFixed(2);
            const totalNum = +(subtotalNum + tipNum + taxNum).toFixed(2);
            const baseBalance = Math.max(
              0,
              Number(paymentBooking.price || 0) -
                (Number(paymentBooking.discount_applied) || 0) -
                (Number(paymentBooking.deposit_paid_amount) || 0)
            );

            const METHOD_META: Record<string, { label: string; icon: any }> = {
              cash: { label: "Cash", icon: Banknote },
              card: { label: "Card Terminal", icon: CreditCard },
              tap_to_pay: { label: "Tap to Pay", icon: Smartphone },
              zelle: { label: "Zelle", icon: Smartphone },
              venmo: { label: "Venmo", icon: Smartphone },
              cashapp: { label: "Cash App", icon: Smartphone },
              other: { label: "Other", icon: Smartphone },
            };
            const methods = acceptedMethods
              .filter((m) => METHOD_META[m])
              .map((m) => ({ value: m, ...METHOD_META[m] }));

            return (
              <div className="space-y-4 py-2">
                {/* Price Summary */}
                <div className="border border-border/50 rounded-md p-3 space-y-1.5 text-xs bg-background/50">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Service Price</span>
                    <span>${Number(paymentBooking.price).toFixed(2)}</span>
                  </div>
                  {Number(paymentBooking.discount_applied) > 0 && (
                    <div className="flex justify-between text-primary">
                      <span>Discount</span>
                      <span>-${Number(paymentBooking.discount_applied).toFixed(2)}</span>
                    </div>
                  )}
                  {Number(paymentBooking.deposit_paid_amount) > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>Deposit Paid</span>
                      <span>-${Number(paymentBooking.deposit_paid_amount).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold border-t border-border pt-1.5 text-sm">
                    <span>Suggested Balance</span>
                    <span>${baseBalance.toFixed(2)}</span>
                  </div>
                </div>

                {/* Editable subtotal */}
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-1 block">
                    Amount to Collect
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={paySubtotal}
                      onChange={(e) => setPaySubtotal(e.target.value)}
                      className="pl-7 bg-background border-border/50"
                    />
                  </div>
                </div>

                {/* Tip presets */}
                {tipPresets.length > 0 && (
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2 block">
                      Tip
                    </label>
                    <div className="flex gap-1.5 flex-wrap mb-2">
                      <button
                        type="button"
                        onClick={() => setPayTip("0")}
                        className={cn(
                          "px-3 py-1.5 text-xs rounded-md border transition-all",
                          Number(payTip) === 0
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border/50 text-muted-foreground hover:border-primary/40"
                        )}
                      >
                        None
                      </button>
                      {tipPresets.map((pct) => {
                        const tipVal = +(subtotalNum * (pct / 100)).toFixed(2);
                        const isActive = Math.abs(Number(payTip) - tipVal) < 0.005 && tipVal > 0;
                        return (
                          <button
                            key={pct}
                            type="button"
                            onClick={() => setPayTip(tipVal.toFixed(2))}
                            className={cn(
                              "px-3 py-1.5 text-xs rounded-md border transition-all",
                              isActive
                                ? "border-primary bg-primary/10 text-primary"
                                : "border-border/50 text-muted-foreground hover:border-primary/40"
                            )}
                          >
                            {pct}% (${tipVal.toFixed(2)})
                          </button>
                        );
                      })}
                    </div>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={payTip}
                        onChange={(e) => setPayTip(e.target.value)}
                        placeholder="Custom tip"
                        className="pl-7 bg-background border-border/50"
                      />
                    </div>
                  </div>
                )}

                {/* Receipt summary */}
                <div className="border border-primary/30 bg-primary/5 rounded-md p-3 space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span>${subtotalNum.toFixed(2)}</span>
                  </div>
                  {tipNum > 0 && (
                    <div className="flex justify-between">
                      <span>Tip</span>
                      <span>${tipNum.toFixed(2)}</span>
                    </div>
                  )}
                  {payTaxRate > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>Tax ({payTaxRate}%)</span>
                      <span>${taxNum.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-base border-t border-primary/30 pt-1.5 text-primary">
                    <span>Total</span>
                    <span>${totalNum.toFixed(2)}</span>
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">Payment Method</p>
                  <div className="grid grid-cols-3 gap-2">
                    {methods.map((method) => (
                      <button
                        key={method.value}
                        onClick={() => setPaymentMethod(method.value)}
                        className={cn(
                          "flex flex-col items-center gap-1 p-2.5 rounded-md border transition-all text-[11px]",
                          paymentMethod === method.value
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border/50 text-muted-foreground hover:border-primary/50 hover:text-foreground"
                        )}
                      >
                        <method.icon size={16} />
                        {method.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Card Terminal: Stripe QR + Copy link (visible after Stripe checkout is generated) */}
                {paymentMethod === "card" && stripeCheckoutUrl && (
                  <div className="border border-primary/40 bg-primary/5 rounded-md p-4 space-y-3">
                    <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-primary">
                      <CreditCard size={14} />
                      Stripe Checkout Ready
                    </div>
                    <div className="flex justify-center bg-background p-3 rounded-md">
                      <QRCodeSVG value={stripeCheckoutUrl} size={160} level="M" />
                    </div>
                    <p className="text-[11px] text-muted-foreground text-center leading-snug">
                      Have the client scan this QR code to pay on their phone, or share the link below.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex-1 text-[11px] gap-1"
                        onClick={() => {
                          navigator.clipboard.writeText(stripeCheckoutUrl);
                          toast({ title: "Copied", description: "Stripe link copied to clipboard." });
                        }}
                      >
                        <Copy size={12} /> Copy Link
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex-1 text-[11px] gap-1"
                        onClick={() => window.open(stripeCheckoutUrl, "_blank")}
                      >
                        <ExternalLink size={12} /> Open
                      </Button>
                    </div>
                  </div>
                )}

                {/* Tap to Pay (Stripe Terminal): kicks off a card_present PaymentIntent.
                    Native iOS/Android Stripe Terminal SDK handles the actual NFC tap; this
                    button is the web-side trigger so the flow can be tested end-to-end. */}
                {paymentMethod === "tap_to_pay" && (
                  <div className="border border-primary/40 bg-primary/5 rounded-md p-4 space-y-3">
                    <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-primary">
                      <Smartphone size={14} />
                      Tap to Pay (Stripe Terminal)
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-snug">
                      Starts a card-present PaymentIntent on Stripe. The native app's Stripe Terminal SDK
                      will pick it up to collect the client's NFC tap.
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      className="w-full text-[11px] gap-1"
                      onClick={handleStartTapToPay}
                      disabled={tapToPayLoading || !(Number(paySubtotal) > 0 || Number(payTip) > 0)}
                    >
                      {tapToPayLoading ? <Loader2 size={12} className="animate-spin" /> : <Smartphone size={12} />}
                      Start Tap to Pay
                    </Button>
                  </div>
                )}


                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-1 block">
                    Notes <span className="text-muted-foreground/60 normal-case tracking-normal">(optional)</span>
                  </label>
                  <Input
                    value={payNotes}
                    onChange={(e) => setPayNotes(e.target.value)}
                    placeholder="e.g. Zelle confirmation #, last 4 of card…"
                    className="bg-background border-border/50 text-sm"
                  />
                </div>

                {/* Collecting payment always marks the booking completed */}
                <div className="flex items-center gap-2 p-3 border border-emerald-500/30 rounded-md bg-emerald-500/5">
                  <CheckCircle size={14} className="text-emerald-600 shrink-0" />
                  <p className="text-[11px] text-muted-foreground">
                    This booking will be marked as <span className="text-emerald-600 font-medium">Completed</span> once payment is collected.
                  </p>
                </div>
              </div>
            );
          })()}

          <DialogFooter className="flex gap-2">
            <Button variant="ghost" onClick={() => { setShowPaymentDialog(false); setPaymentBooking(null); }} className="text-xs tracking-widest uppercase text-muted-foreground">
              Cancel
            </Button>
            <Button
              disabled={paymentLoading || !paymentMethod || !(Number(paySubtotal) > 0 || Number(payTip) > 0)}
              onClick={() => handlePaymentComplete()}
              className="text-xs tracking-widest uppercase gap-1"
            >
              {paymentLoading ? <Loader2 size={14} className="animate-spin" /> : (
                <>
                  <CheckCircle size={14} />
                  {paymentMethod === "card" ? (stripeCheckoutUrl ? "Confirm Payment" : "Generate Card Checkout") : completeAfterPayment ? "Complete & Collect" : "Collect Payment"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tap to Pay waiting modal — placeholder for the native Stripe Terminal SDK.
          Once the iOS/Android build is wired, the SDK confirms the PaymentIntent and
          we'll auto-call handleSimulateTapSuccess (rename to onTapConfirmed) from the
          native bridge instead of needing the manual button. */}
      <Dialog open={tapToPayWaiting} onOpenChange={(open) => { if (!open) setTapToPayWaiting(false); }}>
        <DialogContent className="bg-card border-border/50 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-bold italic flex items-center gap-2">
              <Smartphone size={20} className="text-primary" />
              Waiting for Native SDK
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              Hand the device to the client and have them tap their card.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex flex-col items-center gap-3 py-6 border border-primary/30 bg-primary/5 rounded-md">
              <Loader2 size={36} className="animate-spin text-primary" />
              <p className="text-xs tracking-[0.2em] uppercase text-primary">Listening for tap…</p>
              {tapToPayIntentId && (
                <p className="text-[10px] text-muted-foreground font-mono break-all px-4 text-center">
                  {tapToPayIntentId}
                </p>
              )}
            </div>

            <div className="border border-border/50 rounded-md p-3 bg-background/50 text-[11px] text-muted-foreground leading-relaxed">
              <p className="font-medium text-foreground mb-1">Native build required</p>
              <p>
                On the iOS/Android app, the Stripe Terminal SDK uses the client_secret above to
                collect the tap and confirm the PaymentIntent. Use the simulate button below to
                test the rest of the flow on web.
              </p>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => setTapToPayWaiting(false)}
              className="text-xs tracking-widest uppercase text-muted-foreground"
              disabled={paymentLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSimulateTapSuccess}
              disabled={paymentLoading}
              className="text-xs tracking-widest uppercase gap-1"
            >
              {paymentLoading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
              Simulate Tap Success
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <BlockDateDialog
        selectedDate={dateFilter ? parse(dateFilter, "yyyy-MM-dd", new Date()) : new Date()}
        onSave={() => {
          setBlockDialogOpen(false);
          fetchBookings();
        }}
        open={blockDialogOpen}
        onOpenChange={setBlockDialogOpen}
      />

      <AddonRequestDialog
        booking={addonBooking}
        open={!!addonBooking}
        onOpenChange={(o) => !o && setAddonBooking(null)}
        asAdmin
        onSubmitted={fetchBookings}
      />

      <ReviewQRDialog
        open={reviewQrOpen}
        onOpenChange={setReviewQrOpen}
        bookingId={reviewQrBookingId}
      />
    </div>
  );
};

export default Bookings;
